import type { Components } from 'react-markdown';
import type { Options } from 'rehype-sanitize';
import type { MarkdownViewerProps } from './const';
import classNames from 'classnames';
import { Suspense } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeHighlight from 'rehype-highlight';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import remarkBreaks from 'remark-breaks';
import remarkCjkFriendly from 'remark-cjk-friendly';
import remarkGfm from 'remark-gfm';
import themeStyles from '../theme/styles.module.less';
import {
  generateCustomComponents,
  getCustomTagAttributes,
  getCustomTagNames,
} from './CustomTagCom';
import { preprocessMarkdownContent } from './helper';
import 'highlight.js/styles/github-dark.css';

interface HastNode {
  type?: string;
  tagName?: string;
  value?: string;
  children?: HastNode[];
}

const customTagNames = getCustomTagNames();
const customTagNameSet = new Set(customTagNames);

function isWhitespaceTextNode(node: HastNode): boolean {
  return node.type === 'text' && !node.value?.trim();
}

function getMeaningfulChildren(node?: HastNode): HastNode[] {
  return node?.children?.filter(child => !isWhitespaceTextNode(child)) ?? [];
}

function hasCustomTagChild(node?: HastNode): boolean {
  return getMeaningfulChildren(node).some(child => (
    child.type === 'element' && !!child.tagName && customTagNameSet.has(child.tagName)
  ));
}

function isOnlyCustomTagParagraph(node?: HastNode): boolean {
  const meaningfulChildren = getMeaningfulChildren(node);
  const onlyChild = meaningfulChildren[0];

  return (
    meaningfulChildren.length === 1
    && onlyChild?.type === 'element'
    && !!onlyChild.tagName
    && customTagNameSet.has(onlyChild.tagName)
  );
}

const sanitizeOptions: Options = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    code: [
      ...(defaultSchema.attributes?.code || []),
      'className',
    ],
    pre: [
      ...(defaultSchema.attributes?.pre || []),
      'className',
    ],
    span: [
      ...(defaultSchema.attributes?.span || []),
      'className',
    ],
    ...getCustomTagAttributes(),
  },
  tagNames: [
    ...(defaultSchema.tagNames || []),
    'span',
    ...customTagNames,
  ],
};

const baseComponents: Components = {
  p({ children, node, className, ...props }) {
    const paragraphNode = node as HastNode | undefined;

    if (isOnlyCustomTagParagraph(paragraphNode)) {
      return <>{children}</>;
    }

    if (hasCustomTagChild(paragraphNode)) {
      const blockClassName = classNames(themeStyles['markdown-block-paragraph'], className);
      return (
        <div className={blockClassName} {...props}>
          {children}
        </div>
      );
    }

    return (
      <p className={className} {...props}>
        {children}
      </p>
    );
  },
  a({ children, href, className, ...props }) {
    const mergedClassName = classNames(themeStyles['markdown-link'], className);
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={mergedClassName}
        {...props}
      >
        {children}
      </a>
    );
  },
  ...generateCustomComponents(),
};

export default function MarkdownViewer({
  content,
  className = '',
  components: componentsOverride,
  children,
}: MarkdownViewerProps) {
  const containerClassName = classNames(themeStyles['markdown-container'], className);
  const components: Components = componentsOverride
    ? { ...baseComponents, ...componentsOverride }
    : baseComponents;

  const processedContent = preprocessMarkdownContent(content);

  return (
    <div className={containerClassName}>
      <Suspense fallback={null}>
        <ReactMarkdown
          // Require paired tildes for strikethrough. A single `~` is commonly
          // used by the model as punctuation and should remain visible text.
          remarkPlugins={[remarkCjkFriendly, [remarkGfm, { singleTilde: false }], remarkBreaks]}
          rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizeOptions], rehypeHighlight]}
          components={components}
        >
          {processedContent}
        </ReactMarkdown>
      </Suspense>
      {children}
    </div>
  );
}
